import React, { useState, useEffect, useRef } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet, Platform, Image, ActivityIndicator, Animated } from 'react-native';
import WebScrollView from '../components/WebScrollView';
import { Bot, Send, Loader2 } from '../lib/icons';
import { supabase } from '../lib/supabase';
import { getAccessibleHomes } from '../lib/homeMembership';
import ChatMessage from '../components/chat/ChatMessage';
import BottomSheet from '../components/ui/BottomSheet';
import HoneyDoListBottomSheet from '../components/bottomsheets/HoneyDoListBottomSheet';

interface Message {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  created_at?: string;
  thread_id?: string;
  assistant_message_id?: string;
  run_id?: string;
  actions?: Array<{
    type: 'viewInventory' | 'viewInventoryItem' | 'viewMaintenancePlan' | 'createServiceRequest' | 'viewHoneyDoList';
    data?: {
      itemId?: string;
      itemName?: string;
    };
  }>;
}

interface Profile {
  full_name: string;
  phone_number: string;
  plan: string;
  created_at: string;
}

interface Home {
  id: string;
  address: string;
  city: string;
  state: string;
}

interface InventoryItem {
  id: string;
  name: string;
  category: string;
  model_number: string;
  purchase_date: string;
  purchase_price: number;
}

const Assistant = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<Profile | null>(null);
  const [userHomes, setUserHomes] = useState<Home[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [avatarError, setAvatarError] = useState(false);
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(null);
  const [bottomSheetType, setBottomSheetType] = useState<string | null>(null);
  const [dynamicBottomPadding, setDynamicBottomPadding] = useState(120);
  const [displayedMessageCount, setDisplayedMessageCount] = useState(1);
  const scrollViewRef = useRef<any>(null);
  const bounceAnim1 = useRef(new Animated.Value(0)).current;
  const bounceAnim2 = useRef(new Animated.Value(0)).current;
  const bounceAnim3 = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Promise.all([
      fetchUserData(),
      fetchOrCreateThread()
    ]).finally(() => {
      setInitialLoading(false);
    });
  }, []);

  // Calculate dynamic bottom padding to show only the last message when scrolled to bottom
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const calculatePadding = () => {
      // Get viewport height
      const viewportHeight = window.innerHeight;
      
      // Calculate fixed elements heights
      const navbarHeight = 64; // Fixed navbar height
      const inputHeight = 80; // Approximate input container height
      const headerHeight = 73; // Header with avatar and title
      const topPadding = 16; // Top content padding
      
      // Available scroll area = viewport - navbar - input - header - top padding
      const availableHeight = viewportHeight - navbarHeight - inputHeight - headerHeight - topPadding;
      
      // Use this as bottom padding to push last message to top when at bottom
      setDynamicBottomPadding(Math.max(availableHeight, 120)); // Minimum 120px
    };

    calculatePadding();
    
    // Recalculate on window resize
    window.addEventListener('resize', calculatePadding);
    return () => window.removeEventListener('resize', calculatePadding);
  }, []);

  // Reset displayed message count when new messages arrive
  useEffect(() => {
    if (messages.length > 0) {
      // Show only the most recent message when new messages arrive
      setDisplayedMessageCount(1);
    }
  }, [messages.length]);

  // Scroll to bottom after 1 second when messages load
  useEffect(() => {
    if (messages.length > 0 && !initialLoading) {
      const timer = setTimeout(() => {
        scrollToBottom();
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [messages, initialLoading]);

  useEffect(() => {
    if (loading) {
      const bounceAnimation = Animated.loop(
        Animated.sequence([
          Animated.timing(bounceAnim1, {
            toValue: 1,
            duration: 400,
            useNativeDriver: true,
          }),
          Animated.timing(bounceAnim1, {
            toValue: 0,
            duration: 400,
            useNativeDriver: true,
          }),
        ])
      );
      const bounceAnimation2 = Animated.loop(
        Animated.sequence([
          Animated.delay(200),
          Animated.timing(bounceAnim2, {
            toValue: 1,
            duration: 400,
            useNativeDriver: true,
          }),
          Animated.timing(bounceAnim2, {
            toValue: 0,
            duration: 400,
            useNativeDriver: true,
          }),
        ])
      );
      const bounceAnimation3 = Animated.loop(
        Animated.sequence([
          Animated.delay(400),
          Animated.timing(bounceAnim3, {
            toValue: 1,
            duration: 400,
            useNativeDriver: true,
          }),
          Animated.timing(bounceAnim3, {
            toValue: 0,
            duration: 400,
            useNativeDriver: true,
          }),
        ])
      );
      bounceAnimation.start();
      bounceAnimation2.start();
      bounceAnimation3.start();
    }
  }, [loading]);

  const scrollToBottom = () => {
    if (Platform.OS === 'web' && scrollViewRef.current) {
      // Get the scroll container (WebScrollView's div)
      const scrollViewNode = scrollViewRef.current as any;
      let scrollContainer: HTMLElement | null = null;
      
      if (scrollViewNode?._nativeNode) {
        scrollContainer = scrollViewNode._nativeNode;
      } else if (scrollViewNode?.getNode) {
        scrollContainer = scrollViewNode.getNode();
      } else if (scrollViewNode instanceof HTMLElement) {
        scrollContainer = scrollViewNode;
      }
      
      // Scroll to maximum position with smooth behavior
      if (scrollContainer) {
        scrollContainer.scrollTo({
          top: scrollContainer.scrollHeight,
          behavior: 'smooth'
        });
      }
    } else {
      // Fallback to scrollToEnd for native platforms
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }
  };

  const handleLoadMore = () => {
    if (displayedMessageCount < messages.length) {
      // Load 5 more messages
      setDisplayedMessageCount(prev => Math.min(prev + 5, messages.length));
    }
  };

  const handleBottomSheetOpen = (type: string) => {
    setBottomSheetType(type);
  };

  const handleBottomSheetClose = () => {
    setBottomSheetType(null);
  };

  const fetchOrCreateThread = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: threads, error: threadsError } = await supabase
        .from('threads')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1);

      if (threadsError) throw threadsError;

      let threadId = threads?.[0]?.assistant_thread_id;

      if (threadId) {
        setCurrentThreadId(threadId);
        const { data: messages, error: messagesError } = await supabase
          .from('assistant_messages')
          .select('*')
          .eq('thread_id', threadId)
          .order('created_at', { ascending: true });

        if (messagesError) throw messagesError;
        setMessages(messages || []);
      }
    } catch (error) {
      console.error('Error fetching thread:', error);
    }
  };

  const fetchUserData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profile) {
        setUserProfile(profile);
      }

      const homes = await getAccessibleHomes(user.id);

      if (homes) {
        setUserHomes(homes);
        
        const { data: inventory } = await supabase
          .from('inventory_items')
          .select('*')
          .in('home_id', homes.map(home => home.id));

        if (inventory) {
          setInventoryItems(inventory);
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const handleSubmit = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    
    const newUserMessage: Message = {
      role: 'user',
      content: userMessage,
      thread_id: currentThreadId || undefined
    };
    
    setMessages(prev => [...prev, newUserMessage]);
    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No authenticated session');
      }

      const response = await fetch('https://wqznjpzkejoxiokudivc.supabase.co/functions/v1/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          message: userMessage,
          userProfile,
          userHomes,
          inventoryItems,
          threadId: currentThreadId
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      
      if (data.threadId && !currentThreadId) {
        setCurrentThreadId(data.threadId);
        
        const { error: threadError } = await supabase
          .from('threads')
          .insert([{
            assistant_thread_id: data.threadId,
            user_id: session.user.id
          }]);

        if (threadError) throw threadError;
      }

      const { error: userMessageError } = await supabase
        .from('assistant_messages')
        .insert([{
          thread_id: data.threadId,
          role: 'user',
          content: userMessage
        }]);

      if (userMessageError) throw userMessageError;

      const actions = processResponseForActions(data.message);
      
      const assistantMessage: Message = {
        role: 'assistant',
        content: data.message,
        thread_id: data.threadId,
        assistant_message_id: data.messageId,
        run_id: data.runId,
        actions
      };
      
      setMessages(prev => [...prev, assistantMessage]);

      const { error: assistantMessageError } = await supabase
        .from('assistant_messages')
        .insert([{
          thread_id: data.threadId,
          role: 'assistant',
          content: data.message,
          assistant_message_id: data.messageId,
          run_id: data.runId
        }]);

      if (assistantMessageError) throw assistantMessageError;
      
    } catch (error) {
      console.error('Error getting response:', error);
      const errorMessage: Message = {
        role: 'assistant',
        content: 'I apologize, but I encountered an error. Please try again later.'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const processResponseForActions = (message: string) => {
    const actions: Message['actions'] = [];
    
    const honeyDoKeywords = [
      'honeydolist',
      'honey do list',
      'honey-do list',
      'task list',
      'tasks',
      'todo',
      'to-do',
      'projects',
      'home projects'
    ];
    
    const hasHoneyDoKeywords = honeyDoKeywords.some(keyword => 
      message.toLowerCase().includes(keyword)
    );

    if (hasHoneyDoKeywords) {
      actions.push({ type: 'viewHoneyDoList' });
    }
    
    const maintenanceKeywords = [
      'maintenance',
      'maintenance plan',
      'upcoming tasks',
      'scheduled maintenance',
      'maintenance schedule',
      'maintenance history'
    ];
    
    const hasMaintenanceKeywords = maintenanceKeywords.some(keyword => 
      message.toLowerCase().includes(keyword)
    );

    if (hasMaintenanceKeywords) {
      actions.push({ type: 'viewMaintenancePlan' });
    }

    const inventoryKeywords = ['inventory', 'items', 'belongings', 'possessions'];
    const hasInventoryKeywords = inventoryKeywords.some(keyword => 
      message.toLowerCase().includes(keyword)
    );

    if (hasInventoryKeywords) {
      actions.push({ type: 'viewInventory' });

      inventoryItems.forEach(item => {
        if (message.toLowerCase().includes(item.name.toLowerCase())) {
          actions.push({
            type: 'viewInventoryItem',
            data: {
              itemId: item.id,
              itemName: item.name
            }
          });
        }
      });
    }

    const serviceKeywords = [
      'need service',
      'need help',
      'need repair',
      'broken',
      'not working',
      'fix',
      'repair',
      'service',
      'maintenance needed',
      'problem with',
      'issue with',
      'schedule service',
      'book service',
      'request service'
    ];

    const hasServiceKeywords = serviceKeywords.some(keyword =>
      message.toLowerCase().includes(keyword)
    );

    if (hasServiceKeywords) {
      actions.push({ type: 'createServiceRequest' });
    }

    return actions;
  };

  const renderSkeletonLoader = () => {
    return (
      <View style={styles.skeletonContainer}>
        {[1, 2, 3].map((i) => (
          <View key={i} style={styles.skeletonItem}>
            <View style={styles.skeletonAvatar} />
            <View style={styles.skeletonContent}>
              <View style={styles.skeletonBubble}>
                <View style={styles.skeletonLine} />
                <View style={[styles.skeletonLine, { width: '50%' }]} />
                <View style={[styles.skeletonLine, { width: '66%' }]} />
              </View>
              <View style={styles.skeletonActions}>
                <View style={styles.skeletonButton} />
                <View style={styles.skeletonButton} />
              </View>
            </View>
          </View>
        ))}
      </View>
    );
  };

  const avatarUrl = "https://storage.googleapis.com/hdwebsite/images/HoneyBot%20Avatar.png";

  return (
    <View style={styles.container}>
      <View style={styles.chatContainer}>
        <View style={styles.header}>
          <View style={styles.avatarContainer}>
            {!avatarError ? (
              <Image
                source={{ uri: avatarUrl }}
                style={styles.avatar}
                onError={() => setAvatarError(true)}
              />
            ) : (
              <View style={styles.avatarFallback}>
                <Bot size={24} color="#ffffff" />
              </View>
            )}
          </View>
          <View style={styles.headerText}>
            <Text style={styles.headerTitle}>HoneyBot</Text>
            <Text style={styles.headerSubtitle}>AI Home Assistant</Text>
          </View>
        </View>

        <WebScrollView
          ref={scrollViewRef}
          style={styles.messagesContainer}
          contentContainerStyle={[styles.messagesContent, { paddingBottom: dynamicBottomPadding }]}
        >
          {initialLoading ? (
            renderSkeletonLoader()
          ) : messages.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Bot size={48} color="#9ca3af" />
              <Text style={styles.emptyTitle}>Welcome to HoneyBot!</Text>
              <Text style={styles.emptyText}>
                I'm here to help you manage your home. Ask me anything about maintenance,
                repairs, or your home management needs.
              </Text>
            </View>
          ) : (
            <>
              {/* Show button to load more messages if there are older messages */}
              {displayedMessageCount < messages.length && (
                <Pressable 
                  style={styles.loadMoreButton}
                  onPress={handleLoadMore}
                >
                  <Text style={styles.loadMoreText}>
                    Load older messages
                  </Text>
                  <Text style={styles.loadMoreSubtext}>
                    ({messages.length - displayedMessageCount} total remaining)
                  </Text>
                </Pressable>
              )}
              {/* Display only the most recent N messages */}
              {messages.slice(-displayedMessageCount).map((message, index) => (
                <ChatMessage
                  key={message.id || index}
                  role={message.role}
                  content={message.content}
                  avatarUrl={message.role === 'assistant' ? avatarUrl : undefined}
                  onAvatarError={() => setAvatarError(true)}
                  actions={message.actions}
                  onBottomSheetOpen={handleBottomSheetOpen}
                />
              ))}
              {loading && (
                <View style={styles.loadingMessage}>
                  <View style={styles.loadingAvatar}>
                    {!avatarError ? (
                      <Image
                        source={{ uri: avatarUrl }}
                        style={styles.loadingAvatarImage}
                        onError={() => setAvatarError(true)}
                      />
                    ) : (
                      <View style={styles.loadingAvatarFallback}>
                        <Bot size={20} color="#ffffff" />
                      </View>
                    )}
                  </View>
                  <View style={styles.loadingBubble}>
                    <View style={styles.loadingDots}>
                      <Animated.View
                        style={[
                          styles.loadingDot,
                          {
                            transform: [{
                              translateY: bounceAnim1.interpolate({
                                inputRange: [0, 1],
                                outputRange: [0, -8]
                              })
                            }]
                          }
                        ]}
                      />
                      <Animated.View
                        style={[
                          styles.loadingDot,
                          {
                            transform: [{
                              translateY: bounceAnim2.interpolate({
                                inputRange: [0, 1],
                                outputRange: [0, -8]
                              })
                            }]
                          }
                        ]}
                      />
                      <Animated.View
                        style={[
                          styles.loadingDot,
                          {
                            transform: [{
                              translateY: bounceAnim3.interpolate({
                                inputRange: [0, 1],
                                outputRange: [0, -8]
                              })
                            }]
                          }
                        ]}
                      />
                    </View>
                  </View>
                </View>
              )}
            </>
          )}
        </WebScrollView>

        <View style={styles.inputContainer}>
          <TextInput
            style={styles.input}
            value={input}
            onChangeText={setInput}
            placeholder="Type your message..."
            placeholderTextColor="#9ca3af"
            editable={!loading && !initialLoading}
            onSubmitEditing={handleSubmit}
            returnKeyType="send"
          />
          <Pressable
            onPress={handleSubmit}
            disabled={loading || initialLoading || !input.trim()}
            style={[styles.sendButton, (loading || initialLoading || !input.trim()) && styles.sendButtonDisabled]}
          >
            {loading ? (
              <ActivityIndicator size="small" color="#ffffff" />
            ) : (
              <Send size={20} color="#ffffff" />
            )}
          </Pressable>
        </View>
      </View>

      <BottomSheet
        isOpen={bottomSheetType === 'honeyDoList'}
        onClose={handleBottomSheetClose}
        title="HoneyDone List"
        height="half"
      >
        <HoneyDoListBottomSheet 
          onCreateTask={() => {
            handleBottomSheetClose();
          }}
        />
      </BottomSheet>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    ...(Platform.OS === 'web' && {
      minHeight: 'var(--app-height)',
    }),
  },
  chatContainer: {
    flex: 1,
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      maxHeight: '100%',
      overflow: 'hidden',
    }),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
    backgroundColor: '#ffffff',
  },
  avatarContainer: {
    width: 40,
    height: 40,
    backgroundColor: '#000000',
    borderRadius: 20,
    overflow: 'hidden',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatar: {
    width: '100%',
    height: '100%',
  },
  avatarFallback: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerText: {
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  headerSubtitle: {
    fontSize: 14,
    color: '#4b5563',
  },
  messagesContainer: {
    flex: 1,
    ...(Platform.OS === 'web' && {
      minHeight: 0,
      maxHeight: '100%',
    }),
  },
  messagesContent: {
    padding: 16,
    paddingBottom: 120, // Default padding, overridden by dynamic calculation for "last message only" effect
    gap: 16,
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 48,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '500',
    color: '#111827',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    color: '#4b5563',
    textAlign: 'center',
    paddingHorizontal: 32,
  },
  loadMoreButton: {
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginBottom: 16,
    backgroundColor: '#f3f4f6',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  loadMoreText: {
    fontSize: 14,
    color: '#111827',
    fontWeight: '600',
  },
  loadMoreSubtext: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 4,
  },
  loadingMessage: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  loadingAvatar: {
    width: 32,
    height: 32,
    backgroundColor: '#000000',
    borderRadius: 16,
    overflow: 'hidden',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingAvatarImage: {
    width: '100%',
    height: '100%',
  },
  loadingAvatarFallback: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingBubble: {
    flex: 1,
    backgroundColor: '#f9fafb',
    borderRadius: 12,
    padding: 16,
  },
  loadingDots: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  loadingDot: {
    width: 8,
    height: 8,
    backgroundColor: '#9ca3af',
    borderRadius: 4,
  },
  skeletonContainer: {
    gap: 32,
  },
  skeletonItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  skeletonAvatar: {
    width: 32,
    height: 32,
    backgroundColor: '#e5e7eb',
    borderRadius: 16,
  },
  skeletonContent: {
    flex: 1,
  },
  skeletonBubble: {
    backgroundColor: '#f3f4f6',
    borderRadius: 12,
    padding: 16,
    gap: 8,
  },
  skeletonLine: {
    height: 16,
    backgroundColor: '#e5e7eb',
    borderRadius: 4,
    width: '75%',
  },
  skeletonActions: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  skeletonButton: {
    height: 32,
    width: 128,
    backgroundColor: '#e5e7eb',
    borderRadius: 8,
  },
  inputContainer: {
    flexDirection: 'row',
    gap: 12,
    padding: 16,
    paddingBottom: Platform.OS === 'web' ? 16 : 32, // More padding on mobile
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      position: 'fixed' as any,
      bottom: 64, // Position above the navbar (navbar height = 64px)
      left: 0,
      right: 0,
      zIndex: 40,
      maxWidth: '100%',
    }),
  },
  input: {
    flex: 1,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 24,
    fontSize: 16,
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none' as any,
    }),
  },
  sendButton: {
    width: 48,
    height: 48,
    backgroundColor: '#000000',
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sendButtonDisabled: {
    opacity: 0.5,
  },
});

export default Assistant;
